import { useEffect, useState, type FormEvent } from 'react';
import type { RolAdmin, UsuarioAdminDto } from '@reembolsables/shared';
import { copy } from '../../copy/es';
import { api, textoDeError } from '../../lib/api';
import { useSesion } from '../sesion';

/** Admin user management (RF-01a, RF-01b), in the mockup's table and field style. */
export function UsuariosPage() {
  const { usuario: yo } = useSesion();
  const c = copy.admin.usuarios;
  const [usuarios, setUsuarios] = useState<UsuarioAdminDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [claveTemporal, setClaveTemporal] = useState<{ correo: string; clave: string } | null>(
    null,
  );
  const [nuevo, setNuevo] = useState({ nombre: '', correo: '', rol: 'consulta' as RolAdmin });

  const cargar = () =>
    api<UsuarioAdminDto[]>('/api/admin/usuarios')
      .then(setUsuarios)
      .catch((e) => setError(textoDeError(e)));
  useEffect(() => {
    void cargar();
  }, []);

  if (yo?.rol !== 'operador') return <p className="sub">{c.soloOperador}</p>;

  async function crear(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await api<{ id: string; claveTemporal: string }>('/api/admin/usuarios', {
        method: 'POST',
        body: JSON.stringify(nuevo),
      });
      setClaveTemporal({ correo: nuevo.correo, clave: r.claveTemporal });
      setNuevo({ nombre: '', correo: '', rol: 'consulta' });
      await cargar();
    } catch (err) {
      setError(textoDeError(err));
    }
  }

  async function accion(u: UsuarioAdminDto, que: 'activar' | 'desactivar' | 'restablecer') {
    setError(null);
    try {
      if (que === 'restablecer') {
        const r = await api<{ claveTemporal: string }>(
          `/api/admin/usuarios/${u.id}/restablecer-clave`,
          { method: 'POST' },
        );
        setClaveTemporal({ correo: u.correo, clave: r.claveTemporal });
      } else {
        await api(`/api/admin/usuarios/${u.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ activo: que === 'activar' }),
        });
      }
      await cargar();
    } catch (err) {
      setError(textoDeError(err));
    }
  }

  return (
    <div>
      <h2>{c.titulo}</h2>
      <p className="sub">{c.subtitulo}</p>
      {error && (
        <div className="ok-box bad" role="alert">
          {error}
        </div>
      )}
      {claveTemporal && (
        <div className="ok-box warn">
          <b>
            {c.claveTemporalTitulo} · {claveTemporal.correo}
          </b>
          <div>
            <code
              style={{
                display: 'inline-block',
                background: '#fff',
                padding: '6px 10px',
                borderRadius: 6,
                margin: '8px 0',
                fontSize: 15,
              }}
            >
              {claveTemporal.clave}
            </code>
          </div>
          {c.claveTemporalNota}{' '}
          <button className="mini" onClick={() => setClaveTemporal(null)}>
            {copy.comun.cerrar}
          </button>
        </div>
      )}
      <table className="mk">
        <thead>
          <tr>
            <th>{c.nombre}</th>
            <th>{c.correo}</th>
            <th>{c.rol}</th>
            <th>{c.estado}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {usuarios.map((u) => (
            <tr key={u.id}>
              <td>{u.nombre}</td>
              <td>{u.correo}</td>
              <td>{copy.admin.rol[u.rol]}</td>
              <td>
                <span className={`pill ${u.activo ? 'p-ok' : 'p-no'}`}>
                  {u.activo ? c.activo : c.inactivo}
                </span>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="mini" onClick={() => void accion(u, 'restablecer')}>
                  {c.restablecer}
                </button>{' '}
                {u.id !== yo.id && (
                  <button
                    className="mini"
                    onClick={() => void accion(u, u.activo ? 'desactivar' : 'activar')}
                  >
                    {u.activo ? c.desactivar : c.activar}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="seccion-lbl">{c.nuevo.toUpperCase()}</div>
      <form
        onSubmit={crear}
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 180 }}>
          <label htmlFor="nombre">{c.nombre}</label>
          <input
            id="nombre"
            placeholder={c.nombre}
            value={nuevo.nombre}
            onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
            required
          />
        </div>
        <div className="field" style={{ margin: 0, flex: 1, minWidth: 220 }}>
          <label htmlFor="correo">{c.correo}</label>
          <input
            id="correo"
            type="email"
            placeholder={c.correo}
            value={nuevo.correo}
            onChange={(e) => setNuevo({ ...nuevo, correo: e.target.value })}
            required
          />
        </div>
        <div className="field" style={{ margin: 0, minWidth: 150 }}>
          <label htmlFor="rol">{c.rol}</label>
          <select
            id="rol"
            value={nuevo.rol}
            onChange={(e) => setNuevo({ ...nuevo, rol: e.target.value as RolAdmin })}
          >
            <option value="consulta">{copy.admin.rol.consulta}</option>
            <option value="operador">{copy.admin.rol.operador}</option>
          </select>
        </div>
        <button className="cta">{c.crear}</button>
      </form>
    </div>
  );
}
